import './Products.css';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {useCallback, useEffect, useRef, useState} from "react";
import Filters from "./sub/Filters/Filters.jsx";
import {Product} from "./sub/Product/Product.jsx";
import {useMediaQuery} from "react-responsive";
import Modal from '../Modal/Modal.jsx';
import {loadProducts} from "../../ProductsHandler.js";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator.jsx";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {Helmet} from "react-helmet-async";

// Maps URL slugs -> Polish category names used in Shopify metafields
const SLUG_TO_CATEGORY = {
    // main categories
    "rzesy":            "Rzęsy",
    "pesety":           "Pęsety",
    "kleje":            "Kleje",
    "preparaty":        "Preparaty",
    "tasmy-i-platki":   "Taśmy i płatki",
    "akcesoria":        "Akcesoria",
    "jednorazowe":      "Jednorazowe",
    "brwi":             "Brwi",
    "laminacja":        "Laminacja",
    "depilacja":        "Depilacja",
    "uv":               "UV",
    "karta-klienta":    "Karta klienta",
    // subcategories — Rzęsy
    "rzesy-czarne":             "Rzęsy czarne",
    "rzesy-brazowe":            "Rzęsy brązowe",
    "rzesy-kolorowe":           "Rzęsy kolorowe",
    // subcategories — Pęsety
    "do-rzes":                  "Do rzęs",
    "do-brwi":                  "Do brwi",
    // subcategories — Kleje
    "kleje-uv":                 "Kleje UV",
    // subcategories — Preparaty
    "szampony":                 "Szampony",
    "primery":                  "Primery",
    "cleanery":                 "Cleanery",
    "boostery":                 "Boostery",
    "removery":                 "Removery",
    // subcategories — Taśmy i płatki
    "tasmy":                    "Taśmy",
    "platki":                   "Płatki",
    "nozyczki":                 "Nożyczki",
    // subcategories — Akcesoria
    "holdery":                  "Holdery",
    "tryskawki":                "Tryskawki",
    "poduszki":                 "Poduszki",
    "wiatraczki":               "Wiatraczki",
    "pojemniczki":              "Pojemniczki",
    "podstawki-na-klej":        "Podstawki na klej",
    "lusterka":                 "Lusterka",
    // subcategories — Jednorazowe
    "aplikatory-i-szczoteczki": "Aplikatory i szczoteczki",
    "maseczki":                 "Maseczki",
    "rekawiczki":               "Rękawiczki",
    "podklady-kosmetyczne":     "Podkłady kosmetyczne",
    "waciki":                   "Waciki",
    // subcategories — Brwi
    "farby":                    "Farby",
    "stylizacja-i-pielegnacja": "Stylizacja i pielęgnacja",
    "preparaty-brwi":           "Preparaty",
    "geometria":                "Geometria",
    "pedzle":                   "Pędzle",
    "akcesoria-brwi":           "Akcesoria",
    // subcategories — Laminacja
    "koreanska-laminacja":      "Koreańska laminacja",
    "laminacja-rzes":           "Laminacja rzęs",
    "laminacja-brwi":           "Laminacja brwi",
    // subcategories — Karta klienta
    "karty-klienta":            "Karty klienta",
};

export default function Products() {
    const [searchParams] = useSearchParams();
    const { pathname, search } = useLocation();
    const { maincat, subcat } = useParams();

    // Applied range (drives fetch) vs. draft range (slider UI)
    const [priceRange, setPriceRange] = useState(null);
    const [priceRangeDraft, setPriceRangeDraft] = useState([1, 1000]);

    // URL = source of truth (support multiple values)
    const kategorie = searchParams.getAll("kategoria");
    const marki = searchParams.getAll("marka");

    // robust nazwa parsing (decode '+' to spaces)
    const nazwaParam = (searchParams.get("nazwa") || "")
        .replace(/\+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    // Prefer query params (for search/filter use cases), fall back to path params
    const mainCategory = (searchParams.get("maincategory") || SLUG_TO_CATEGORY[maincat] || "").trim();
    const subCategory  = (searchParams.get("subcategory")  || SLUG_TO_CATEGORY[subcat]  || "").trim();

    const bestsellerParam = (searchParams.get("bestseller") || "").toLowerCase();
    const promoRaw =
        (searchParams.get("promotion") || searchParams.get("promocje") || searchParams.get("promocja") || "").toLowerCase();
    const newCollectionParam = (searchParams.get("newcollection") || "").toLowerCase();

    // parse flags
    const bestsellerFlag =
        bestsellerParam === "true" ? true :
            bestsellerParam === "false" ? false : null;

    const promocjaFlag =
        promoRaw === "true" ? true :
            promoRaw === "false" ? false : null;

    const newCollectionFlag =
        newCollectionParam === "true" ? true :
            newCollectionParam === "false" ? false : null;

    const [sortType, setSortType] = useState("name-desc");
    const [displayType, setDisplayType] = useState("grid");
    const [displayMobileFilters, setDisplayMobileFilters] = useState(false);
    const [products, setProducts] = useState([]);
    const [endCursor, setEndCursor] = useState(null);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [loading, setLoading] = useState(false);

    const isMobile = useMediaQuery({ maxWidth: 768 });
    const observer = useRef(null);

    const manageDisplay = (type) => {
        if (displayType !== type) setDisplayType(type);
    };

    const fetchProducts = async (after = null, reset = false) => {
        if (loading) return;
        setLoading(true);

        const data = await loadProducts(
            sortType,
            kategorie,
            marki,
            bestsellerFlag,
            promocjaFlag,
            newCollectionFlag,
            priceRange,
            nazwaParam,
            mainCategory,
            subCategory,
            after
        );

        setProducts(prev => (reset ? data.products : [...prev, ...data.products]));
        setEndCursor(data.endCursor);
        setHasNextPage(data.hasNextPage);
        setLoading(false);
    };

    // Close mobile filters whenever filters in URL change
    useEffect(() => {
        setDisplayMobileFilters(false);
    }, [pathname, search]);

    useEffect(() => {
        fetchProducts(null, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sortType,
        JSON.stringify(kategorie),
        JSON.stringify(marki),
        bestsellerParam,
        promoRaw,
        newCollectionParam,
        JSON.stringify(priceRange),
        nazwaParam,
        mainCategory,
        subCategory,
        maincat,
        subcat,
    ]);

    // infinite scroll
    const lastProductRef = useCallback(node => {
        if (loading || !hasNextPage) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) fetchProducts(endCursor);
        });

        if (node) observer.current.observe(node);
    }, [loading, hasNextPage, endCursor]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => { if (observer.current) observer.current.disconnect(); };
    }, []);

    const canonical = `https://lumestore.pl${pathname}${search || ""}`;

    // Flags (for label/title composition)
    const flags = [];
    if (searchParams.get("promocja") === "true" || searchParams.get("promocje") === "true" || searchParams.get("promotion") === "true") flags.push("Promocje");
    if (searchParams.get("bestseller") === "true") flags.push("Bestsellery");
    if (searchParams.get("newcollection") === "true") flags.push("Nowości");

    // ───────────────────────── LABEL (H1) ─────────────────────────
    const labelParts = [];
    if (nazwaParam) {
        labelParts.push(`Wyniki: ${nazwaParam}`);
    } else {
        if (mainCategory) labelParts.push(mainCategory);
        if (subCategory)  labelParts.push(subCategory);
        if (!mainCategory && !subCategory && kategorie.length === 1) labelParts.push(kategorie[0]);
        if (marki.length === 1) labelParts.push(marki[0]);
        if (!labelParts.length && flags.length) labelParts.push(flags.join(" · "));
    }
    const categoryLabel = labelParts.length ? labelParts.join(" › ") : "Produkty";

    // ───────────────────────── TITLE / META ─────────────────────────
    const pageTitle = "Produkty do Stylizacji Rzęs | Lume Store";
    const description = "Profesjonalne produkty dla stylistek rzęs: rzęsy, pęsety, akcesoria i narzędzia. Lume Store – wszystko, czego potrzebujesz do perfekcji.";

    const itemListJsonLd = products.length ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: products.slice(0, 24).map((p, idx) => {
            const productUrl = p.handle
                ? `https://lumestore.pl/produkt/${encodeURIComponent(p.handle)}`
                : `https://lumestore.pl/produkt?id=${encodeURIComponent(p.id)}`;
            return ({
                "@type": "ListItem",
                position: idx + 1,
                url: productUrl
            });
        })
    } : null;

    const breadcrumbs = [
        { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://lumestore.pl/" },
        { "@type": "ListItem", "position": 2, "name": "Produkty", "item": "https://lumestore.pl/produkty" },
    ];
    let pos = 3;
    if (!nazwaParam) {
        if (mainCategory) breadcrumbs.push({ "@type": "ListItem", "position": pos++, "name": mainCategory, "item": canonical });
        if (subCategory)  breadcrumbs.push({ "@type": "ListItem", "position": pos++, "name": subCategory,  "item": canonical });
        if (!mainCategory && !subCategory && kategorie.length === 1) {
            breadcrumbs.push({ "@type": "ListItem", "position": pos++, "name": kategorie[0], "item": canonical });
        }
        if (marki.length === 1) breadcrumbs.push({ "@type": "ListItem", "position": pos++, "name": marki[0], "item": canonical });
        if (flags.length && !(kategorie.length === 1 || marki.length === 1 || mainCategory || subCategory)) {
            breadcrumbs.push({ "@type": "ListItem", "position": pos++, "name": flags.join(" · "), "item": canonical });
        }
    } else {
        breadcrumbs.push({ "@type": "ListItem", "position": pos++, "name": `Wyniki: ${nazwaParam}`, "item": canonical });
    }

    const breadcrumbsJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs
    };

    console.log("PARAMS:", { maincat, subcat });
    console.log("PATHNAME:", pathname);

    return (
        <div id="products">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonical} />
                <meta name="robots" content="index,follow" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonical} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="https://lumestore.pl/og-image.webp" />
                <meta property="og:site_name" content="Lumestore" />
                <meta property="og:locale" content="pl_PL" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content="https://lumestore.pl/og-image.webp" />
                {itemListJsonLd && (
                    <script type="application/ld+json">
                        {JSON.stringify(itemListJsonLd)}
                    </script>
                )}
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbsJsonLd)}
                </script>
            </Helmet>

            {displayMobileFilters && isMobile && (
                <Modal
                    isOpen={displayMobileFilters}
                    onClose={() => setDisplayMobileFilters(false)}
                >
                    <Filters
                        priceRangeDraft={priceRangeDraft}
                        setPriceRangeDraft={setPriceRangeDraft}
                        applyPriceRange={setPriceRange}
                    />
                </Modal>
            )}

            {!isMobile && <div id="products-left">
                <Filters
                    priceRangeDraft={priceRangeDraft}
                    setPriceRangeDraft={setPriceRangeDraft}
                    applyPriceRange={setPriceRange}
                />
            </div>}

            <div id="products-right">
                <div id="products-right-top">
                    <h1 id="products-right-top-label-1">
                        {categoryLabel}
                    </h1>

                    <div id="products-managment-wrapper">
                        <div id="products-display-manager">
                            {!isMobile && (
                                <>
                                    <div
                                        id="products-display-grid-bttn"
                                        className={displayType === 'grid'
                                            ? 'products-display-bttn products-display-bttn--active'
                                            : 'products-display-bttn'}
                                        onClick={() => manageDisplay('grid')}
                                    >
                                        <img loading="lazy" src='/images/grid-icon.webp' alt="Widok siatki" />
                                    </div>
                                    <div
                                        id="products-display-list-bttn"
                                        className={displayType === 'list'
                                            ? 'products-display-bttn products-display-bttn--active'
                                            : 'products-display-bttn'}
                                        onClick={() => manageDisplay('list')}
                                    >
                                        <img loading="lazy" src='/images/list-icon.webp' alt="Widok listy" />
                                    </div>
                                </>
                            )}

                            {isMobile && (
                                displayType === 'list' ? (
                                    <div
                                        id="products-display-list-bttn"
                                        className="products-display-bttn products-display-bttn--active"
                                        onClick={() => manageDisplay('grid')}
                                    >
                                        <img loading="lazy" src='/images/list-icon.webp' alt="" />
                                    </div>
                                ) : (
                                    <div
                                        id="products-display-grid-bttn"
                                        className="products-display-bttn products-display-bttn--active"
                                        onClick={() => manageDisplay('list')}
                                    >
                                        <img loading="lazy" src='/images/list-icon.webp' alt="" />
                                    </div>
                                )
                            )}
                        </div>

                        {isMobile && (
                            <div id="products-mobile-filter" onClick={() => setDisplayMobileFilters(true)}>
                                Filtruj
                            </div>
                        )}

                        <div id="products-sort">
                            <span id="product-sort-label-1">Sortuj wg:</span>
                            <Select
                                variant="outlined"
                                value={sortType}
                                onChange={(e) => setSortType(e.target.value)}
                                sx={{
                                    width: '75%',
                                    height: '30%',
                                    borderRadius: 30,
                                    color: 'rgba(0,0,0,.7)',
                                    backgroundColor: '#F5F5F5',
                                    "& .MuiOutlinedInput-notchedOutline": { border: 'none' },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
                                    "& .MuiPopover-root": { maxWidth: '90vw' }
                                }}
                            >
                                <MenuItem value="name-desc">Nazwa - Od A do Z</MenuItem>
                                <MenuItem value="name-asc">Nazwa - Od Z do A</MenuItem>
                                <MenuItem value="price-desc">Cena - Od najwyższej</MenuItem>
                                <MenuItem value="price-asc">Cena - Od najniższej</MenuItem>
                            </Select>
                        </div>
                    </div>
                </div>

                <div id="products-right-bottom">
                    {products.length > 0 ? products.map((product, index) => {
                        const isLast = index === products.length - 1;
                        return (
                            <Product
                                key={product.id}
                                product={product}
                                display={displayType}
                                ref={isLast ? lastProductRef : null}
                            />
                        );
                    }) : <p className='end-indicator'>Niestety, jeszcze nic tutaj nie ma.</p>}
                    {loading && <LoadingIndicator height={7} />}
                    {!hasNextPage && products.length >= 7 && products.length > 0 && <p className="end-indicator">To już wszystko.</p>}
                </div>
            </div>
        </div>
    );
}